import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { homeApi, rankingsApi } from '../api';
import { Ranking } from '../types';
import '../styles/main.css';

// 添加图标引用
const clickIcon = require('../assets/images/click_48.png');

const Home: React.FC = () => {
  const [featuredRankings, setFeaturedRankings] = useState<Ranking[]>([]);
  const [latestRankings, setLatestRankings] = useState<Ranking[]>([]);
  const [popularRankings, setPopularRankings] = useState<Ranking[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // 添加背景样式
  const pageStyle = {
    background: 'linear-gradient(to bottom, #f8fbff, #f0f7ff)',
    minHeight: '100vh',
    position: 'relative' as const,
  };

  // 添加装饰元素样式
  const decorationStyle = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none' as const,
    overflow: 'hidden' as const,
    zIndex: 0,
  };

  // 添加樱花粒子状态
  const [cherryBlossoms, setCherryBlossoms] = useState<Array<{
    id: number;
    x: number;
    y: number;
    size: number;
    opacity: number;
    speedX: number;
    speedY: number;
    rotation: number;
    rotationSpeed: number;
  }>>([]);
  
  // 生成唯一ID
  const [nextId, setNextId] = useState(1);
  
  // 添加管理员状态检查
  const location = useLocation();
  const isAdmin = new URLSearchParams(location.search).get('admin') === 'y';
  
  // 处理点击事件，创建樱花绽放效果
  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement> | MouseEvent) => {
    const x = e.clientX;
    const y = e.clientY;
    // 明确定义 newBlossoms 的类型
    const newBlossoms: Array<{
      id: number;
      x: number;
      y: number;
      size: number;
      opacity: number;
      speedX: number;
      speedY: number;
      rotation: number;
      rotationSpeed: number;
    }> = [];
    
    // 创建15-25个樱花粒子
    const count = Math.floor(Math.random() * 11) + 15;
    
    for (let i = 0; i < count; i++) {
      // 随机生成樱花粒子的属性
      const size = Math.random() * 5 + 3;
      const opacity = Math.random() * 0.5 + 0.3;
      
      // 随机速度和方向
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 5 + 2;
      const speedX = Math.cos(angle) * speed;
      const speedY = Math.sin(angle) * speed;
      
      // 随机旋转
      const rotation = Math.random() * 360;
      const rotationSpeed = (Math.random() - 0.5) * 10;
      
      newBlossoms.push({
        id: nextId + i,
        x,
        y,
        size,
        opacity,
        speedX,
        speedY,
        rotation,
        rotationSpeed
      });
    }
    
    setNextId(nextId + count);
    setCherryBlossoms(prev => [...prev, ...newBlossoms]);
  }, [nextId]);
  
  // 动画更新函数
  useEffect(() => {
    if (cherryBlossoms.length === 0) return;
    
    const animationFrame = requestAnimationFrame(() => {
      setCherryBlossoms(prev => 
        prev
          .map(blossom => ({
            ...blossom,
            x: blossom.x + blossom.speedX,
            y: blossom.y + blossom.speedY,
            speedY: blossom.speedY + 0.1, // 添加重力效果
            rotation: blossom.rotation + blossom.rotationSpeed,
            opacity: blossom.opacity - 0.01 // 逐渐消失
          }))
          .filter(blossom => 
            blossom.opacity > 0 && 
            blossom.x > -100 && 
            blossom.x < window.innerWidth + 100 && 
            blossom.y < window.innerHeight + 100
          )
      );
    });
    
    return () => cancelAnimationFrame(animationFrame);
  }, [cherryBlossoms]);
  
  // 添加点击事件监听
  useEffect(() => {
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [handleClick]);

  useEffect(() => {
    fetchHomeData();
  }, []);

  const fetchHomeData = async () => {
    try {
      setLoading(true);
      const { data } = await homeApi.getAllHomeData();
      
      setFeaturedRankings(data.featuredRankings || []);
      setLatestRankings(data.latestRankings || []);
      setPopularRankings(data.popularRankings || []);
    } catch (error) {
      console.error('获取首页数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 处理删除榜单
  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.preventDefault(); // 阻止链接跳转
    e.stopPropagation(); // 阻止事件冒泡
    
    if (window.confirm('确定要删除这个榜单吗？删除后无法恢复。')) {
      try {
        await rankingsApi.delete(String(id));
        
        // 从所有列表中移除被删除的榜单
        const filterById = (ranking: Ranking) => ranking.id !== id;
        setFeaturedRankings(prev => prev.filter(filterById));
        setLatestRankings(prev => prev.filter(filterById));
        setPopularRankings(prev => prev.filter(filterById));
        
        alert('删除成功');
      } catch (error) {
        console.error('删除榜单失败:', error);
        alert('删除失败，请重试');
      }
    }
  };

  if (loading) {
    return <div className="loading" style={pageStyle}>加载中...</div>;
  }

  // 处理空数据情况
  if (featuredRankings.length === 0 && latestRankings.length === 0 && popularRankings.length === 0) {
    return (
      <div className="container" style={pageStyle}>
        <div className="empty-state">暂无榜单数据</div>
      </div>
    );
  }

  // 合并所有榜单数据，并去重
  const allRankings = [...featuredRankings, ...latestRankings, ...popularRankings];
  // 使用 Set 和 Map 去重，保留每个 ID 的第一个出现的榜单
  const uniqueRankings = Array.from(
    new Map(allRankings.map(ranking => [ranking.id, ranking])).values()
  );

  return (
    <div style={pageStyle} onClick={handleClick}>
      <div style={decorationStyle}>
        {/* 添加装饰元素 */}
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            {/* 樱花图案 */}
            <pattern id="cherry-blossom" x="0" y="0" width="300" height="300" patternUnits="userSpaceOnUse">
              <circle cx="50" cy="50" r="2" fill="#ffcce6" opacity="0.3" />
              <circle cx="150" cy="70" r="1.5" fill="#ffcce6" opacity="0.25" />
              <circle cx="270" cy="130" r="2" fill="#ffcce6" opacity="0.3" />
              <circle cx="90" cy="220" r="1.5" fill="#ffcce6" opacity="0.25" />
              <circle cx="180" cy="280" r="2" fill="#ffcce6" opacity="0.3" />
              <circle cx="220" cy="40" r="1.5" fill="#ffcce6" opacity="0.25" />
              <circle cx="30" cy="170" r="2" fill="#ffcce6" opacity="0.3" />
              <circle cx="280" cy="190" r="1.5" fill="#ffcce6" opacity="0.25" />
              <circle cx="120" cy="120" r="1.8" fill="#ffcce6" opacity="0.3" />
              <circle cx="240" cy="240" r="1.7" fill="#ffcce6" opacity="0.25" />
            </pattern>
            
            {/* 樱花花瓣形状 */}
            <path id="petal" d="M0,0 C2,-5 8,-10 0,-15 C-8,-10 -2,-5 0,0" fill="#ffcce6" />
          </defs>
          
          {/* 樱花背景 */}
          <rect width="100%" height="100%" fill="url(#cherry-blossom)" />
          
          {/* 动态生成的樱花粒子 */}
          {cherryBlossoms.map(blossom => (
            <g 
              key={blossom.id} 
              transform={`translate(${blossom.x}, ${blossom.y}) rotate(${blossom.rotation})`}
              opacity={blossom.opacity}
            >
              <circle cx="0" cy="0" r={blossom.size / 3} fill="#ffb7d9" />
              <use 
                href="#petal" 
                transform={`scale(${blossom.size / 15})`} 
              />
              <use 
                href="#petal" 
                transform={`scale(${blossom.size / 15}) rotate(72)`} 
              />
              <use 
                href="#petal" 
                transform={`scale(${blossom.size / 15}) rotate(144)`} 
              />
              <use 
                href="#petal" 
                transform={`scale(${blossom.size / 15}) rotate(216)`} 
              />
              <use 
                href="#petal" 
                transform={`scale(${blossom.size / 15}) rotate(288)`} 
              />
            </g>
          ))}
        </svg>
      </div>
      
      <div className="container" style={{ 
        position: 'relative', 
        zIndex: 1,
        maxWidth: '900px',  // 添加最大宽度限制，与详情页一致
        margin: '0 auto',   // 居中显示
        padding: '0 15px'   // 添加内边距
      }}>
        <div className="header">
          
        </div>

        {/* 移除标签页，直接展示所有榜单 */}
        <section className="ranking-section">
          <div className="rankings-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
            gap: '20px',                                                  
            marginTop: '20px'
          }}>
            {uniqueRankings.map(ranking => (
              <Link to={`/ranking/${ranking.id}`} key={ranking.id} className="ranking-card" style={{ 
                background: 'rgba(255, 255, 255, 0.8)', 
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
                borderRadius: '8px',                     
                overflow: 'hidden',                      
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                padding: '15px'                          // 添加内边距，移除之前的溢出隐藏
              }}>
                {isAdmin && (
                  <button 
                    className="delete-btn" 
                    onClick={(e) => handleDelete(e, ranking.id)}
                    title="删除榜单"
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      zIndex: 2,
                      background: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    ×
                  </button>
                )}
                
                {/* 修改布局，将图片作为小型Logo展示 */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  {ranking.coverUrl && (
                    <div style={{ 
                      width: '50px', 
                      height: '50px', 
                      borderRadius: '8px',
                      overflow: 'hidden',
                      marginRight: '12px',
                      flexShrink: 0,
                      border: '1px solid #eee'
                    }}>
                      <img 
                        src={ranking.coverUrl} 
                        alt={ranking.title} 
                        className="ranking-logo" 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover' 
                        }} 
                      />
                    </div>
                  )}
                  
                  <div style={{ flex: 1 }}>
                    <h3 className="ranking-title" style={{ 
                      margin: '0 0 4px 0',
                      fontSize: '15px',
                      fontWeight: '600',
                      lineHeight: '1.3',
                      // 限制标题为两行
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>{ranking.title}</h3>
                    
                    {/* 添加访问次数和更新时间在标题下方 */}
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '12px',
                      color: '#888',
                      justifyContent: 'space-between' // 改为两端对齐
                    }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center'
                      }}>
                        <img 
                          src={clickIcon} 
                          alt="点击" 
                          style={{ 
                            width: 10, 
                            height: 10, 
                            marginRight: 3, 
                            verticalAlign: 'text-bottom',
                            filter: 'invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(90%) contrast(95%)'
                          }} 
                        />
                        {(ranking as any).clkCnt || 0}
                      </span>
                      
                      {/* 添加更新时间，如果有的话，右对齐 */}
                      {ranking.updatedAt && (
                        <span style={{ marginLeft: 'auto' }}> {/* 使用 marginLeft: 'auto' 实现右对齐 */}
                          {new Date(ranking.updatedAt).toLocaleDateString('zh-CN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1,
                  justifyContent: 'space-between'
                }}>
                  <p className="ranking-description" style={{ 
                    margin: '0 0 12px 0',
                    fontSize: '13px',
                    color: '#666',
                    lineHeight: '1.4',
                    // 限制描述为四行，因为现在有更多空间
                    display: '-webkit-box',
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>{ranking.description}</p>
                  
                  <div style={{ marginTop: 'auto' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'flex-start', // 改为左对齐
                      alignItems: 'center'
                    }}>
                      <div className="tags">
                        {ranking.type && <span className="tag" style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          fontSize: '12px',
                          borderRadius: '4px',
                          backgroundColor: '#e9f5ff',
                          color: '#3498db'
                        }}>{ranking.type}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Home; 